import { memo } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface PromptSliderProps {
  text: string;
  weight: number;
  color: { hsl: string; hex: string };
  onWeightChange: (weight: number) => void;
  onDelete: () => void;
}

export const PromptSlider = memo(function PromptSlider({
  text,
  weight,
  color,
  onWeightChange,
  onDelete,
}: PromptSliderProps) {
  const handleSliderChange = ([value]: number[]) => {
    const nextWeight = value === 0 ? (weight < 0 ? -0.01 : 0.01) : value / 100;
    onWeightChange(nextWeight);
  };

  return (
    <div className="group flex items-center gap-3 py-2">
      <span className="text-sm text-foreground/90 truncate min-w-0 flex-shrink-0 max-w-[180px]">
        {text}
      </span>
      <SliderPrimitive.Root
        className="relative flex flex-1 touch-none select-none items-center"
        value={[weight * 100]}
        onValueChange={handleSliderChange}
        min={-100}
        max={100}
        step={1}
      >
        <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-white/10">
          <SliderPrimitive.Range
            className="absolute h-full rounded-full transition-all"
            style={{ backgroundColor: `hsl(${color.hsl})` }}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className="block h-5 w-5 rounded-full border-2 bg-background ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:scale-110"
          style={{ borderColor: `hsl(${color.hsl})` }}
        />
      </SliderPrimitive.Root>
      <span
        className="text-xs font-mono w-10 text-right"
        style={{ color: `hsl(${color.hsl})` }}
      >
        {Math.round(weight * 100)}%
      </span>
      <button
        onClick={onDelete}
        className={cn(
          "opacity-0 group-hover:opacity-100 transition-opacity",
          "h-6 w-6 flex items-center justify-center rounded-full",
          "hover:bg-white/10 text-muted-foreground hover:text-foreground"
        )}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
});
